// @ts-nocheck

import type React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { inline, margin, padding, stacked } from 'app/shared/styles/_spacing';
import { colors } from 'app/shared/styles/_colors';
import { useSelector } from 'react-redux';
import assign from 'lodash/assign';
import mailcheck from 'mailcheck';
import cx from 'classnames';

// Components
import BedsBathsSelect from 'app/shared/modules/hdp/form/Contact/BedsBathsSelect';
import Button from 'app/shared/core/Button';
import Checkbox from 'app/shared/core/Checkbox';
import ContactListingRestrictions from 'app/shared/modules/hdp/form/Contact/ContactListingRestrictions';
import ContactUserInfo from 'app/shared/modules/hdp/form/Contact/ContactUserInfo';
import Input from 'app/shared/core/Input';
import InputPhoneNumber from 'app/shared/modules/InputPhoneNumber';
import Linker from 'app/shared/modules/Linker';
import MoveInCalendar from 'app/shared/modules/MoveInCalendar';
import Row from 'app/shared/core/Row';
import Text from 'app/shared/core/Text';

// Misc / Utils
import './ContactForm.scss';
import { shouldShowRequestToApplyButton } from 'app/shared/utils/cometUtils';
import controller from './controller';
import dateUtils from 'app/shared/utils/dateUtils';
import IconMoreInfo from 'images/icons/more-info-grey.svg';
import IconMoreInfoActive from 'images/icons/more-info-blue.svg';
import { listingUtils_getContactEmail } from 'app/shared/utils/listingUtils';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ListingDetails } from 'app/types/listingDetails.type';

const UPDATE_EMAIL_ERROR_MSG = 'Please update your email address.';

// Selector that combines all ContactForm state needs
const selectContactFormState = (state: RootReduxState) => ({
  clientLoaded: state.app.clientLoaded,
  isMobile: state.app.device.screenWidth === 'sm',
  listing: state.currentListingDetails.currentListing,
  user: state.user,
  userInquiryDataCache: state.user.inquiryDataCache,
  isCachedDataPending: state.user.inquiryDataCache?.isPending || false,
});

interface ContactFormInputValues {
  userMessage: string;
  userEmail: string;
  userName: string;
  userPhoneNumber: string;
  numBaths: string;
  numBeds: string;
  moveDate: string;
  lowIncomeMessage: string | null;
  requestToApply: boolean;
}

interface ContactFormProps {
  clientLoaded?: boolean;
  ctaButtonContext?: string;
  contactSubmitted: boolean;
  dispatch?: (action: unknown) => void;
  emailErrorMessage?: string;
  errorMessage: string;
  handleSetActiveModal?: () => void;
  isCachedDataPending?: boolean;
  isPopup: boolean;
  isLoading: boolean;
  isMobile?: boolean;
  listing: ListingDetails;
  onSetActiveModal?: (activeModal: string) => void;
  onSubmitForm: (inputValues: ContactFormInputValues) => void;
  onClickPhoneContact?: () => void;
  phoneErrorMessage?: string;
  beds?: number | null;
  baths?: number | null;
  user?: {
    loggedIn?: boolean;
    info?: {
      firstName?: string;
      lastName?: string;
    };
  };
  userInquiryDataCache?: {
    text?: string;
    name?: string;
    phone?: string;
    email?: string;
    isPending?: boolean;
  };
  renderModal?: () => React.ReactNode;
}

const ReadyToApplyText = styled(Text)`
  ${inline._1x};
  color: ${colors['$hpx-grey-600']};
  font-weight: bold;
`;
const StyledSubmitContainer = styled.div`
  ${stacked._1x};
  ${margin.top._3x};
`;
const StyledCalendarContainer = styled.div`
  ${stacked._2x};
`;
const StyledContactListingRestrictions = styled(ContactListingRestrictions)`
  ${(props) =>
    props &&
    props.isPopup === false &&
    css`
      ${padding.top._2x};
    `}
`;
const StyledEmailInput = styled(Input)`
  ${padding.top._2x};
`;
const StyledInputPhoneNumber = styled(InputPhoneNumber)`
  ${padding.top._2x};
`;
const StyledCheckbox = styled(Checkbox)`
  color: ${colors['$hpx-blue-600']};
`;
const StyledContactFormTipText = styled.div`
  color: ${colors['$hpx-grey-600']};
`;
const StyledAlertDiv = styled.div`
  color: ${colors['$hpx-red-400']};
`;
const ContactForm: React.FC<ContactFormProps> = (props) => {
  const {
    beds = null,
    baths = null,
    ctaButtonContext,
    contactSubmitted,
    emailErrorMessage,
    errorMessage,
    isLoading,
    isPopup,
    onSubmitForm,
    phoneErrorMessage,
  } = props;

  // Get Redux state using optimized memoized selector
  const reduxState = useSelector(selectContactFormState);

  // Use props if provided, otherwise fall back to Redux
  const clientLoaded = props.clientLoaded ?? reduxState.clientLoaded;
  const isCachedDataPending = props.isCachedDataPending ?? reduxState.isCachedDataPending;
  const isMobile = props.isMobile ?? reduxState.isMobile;
  const listing = props.listing || reduxState.listing;
  const user = props.user || reduxState.user;
  // Only use props.userInquiryDataCache if it has actual cached data (not just isPending flag)
  const userInquiryDataCache =
    props.userInquiryDataCache?.name || props.userInquiryDataCache?.email || props.userInquiryDataCache?.phone
      ? props.userInquiryDataCache
      : reduxState.userInquiryDataCache;

  // Refs
  const userMessageInput = useRef<HTMLInputElement>(null);

  // Helper function to calculate initial values - extracted to avoid duplication
  const calculateInitialValues = useCallback(() => {
    const userEmail = listingUtils_getContactEmail(user, userInquiryDataCache);
    const userInfoFirstLastName =
      user?.info?.firstName && user?.info?.lastName ? `${user.info.firstName} ${user.info.lastName}` : '';

    // Type assertion for listing - we know the structure from the interface
    const typedListing = listing as ContactFormProps['listing'];
    let userMessage = userInquiryDataCache?.text || typedListing?.contact?.defaultInquiryMessage || '';

    if (typedListing?.waitlisted) {
      userMessage = 'Please add me to the waitlist for this listing. Thank you.';
    }

    if (ctaButtonContext === 'message') {
      userMessage = 'I would like more information about this listing. Thank you.';
    }

    const dedupedFloorPlans = controller.dedupeBedsBathsOptions(typedListing?.floorplans || []);

    let numBaths = '';
    let numBeds = '';
    if (beds && baths) {
      numBaths = String(baths);
      numBeds = String(beds);
    } else {
      numBaths = (dedupedFloorPlans[0] && dedupedFloorPlans[0].baths) || '';
      numBeds = (dedupedFloorPlans[0] && dedupedFloorPlans[0].beds) || '';
    }

    return {
      userEmail,
      userInfoFirstLastName,
      userMessage,
      numBaths,
      numBeds,
      dedupedFloorPlans,
    };
  }, [user, userInquiryDataCache, listing, ctaButtonContext, beds, baths]);

  // Memoized initial values
  const initialValues = useMemo(() => calculateInitialValues(), [calculateInitialValues]);

  // State hooks
  const [inputValues, setInputValues] = useState<ContactFormInputValues>({
    userMessage: initialValues.userMessage,
    userEmail: initialValues.userEmail || '',
    userName: userInquiryDataCache?.name || initialValues.userInfoFirstLastName || '',
    userPhoneNumber: userInquiryDataCache?.phone || '',
    numBaths: initialValues.numBaths,
    numBeds: initialValues.numBeds,
    moveDate: dateUtils.formatDateMmddyyyy(new Date()),
    lowIncomeMessage: null,
    requestToApply: false,
  });

  const [inputErrors, setInputErrors] = useState({
    numBaths: null as string | null,
    numBeds: null as string | null,
    moveDate: null as string | null,
    userMessage: null as string | null,
    userEmail: controller.userHasMaskedEmail(initialValues.userEmail) ? UPDATE_EMAIL_ERROR_MSG : null,
    userName: null as string | null,
    userPhoneNumber: null as string | null,
  });

  const [restrictedConfirmation, setRestrictedConfirmation] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingUserInfo, setEditingUserInfo] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [mailcheckHint, setMailcheckHint] = useState<string | React.ReactNode>('');

  // Function to update textarea height
  const updateTextAreaHeight = useCallback(() => {
    const node = userMessageInput.current;
    if (node) {
      // For Input component with type="textarea", look for the actual textarea element
      const textareas = node.getElementsByTagName('textarea');
      if (textareas && textareas.length > 0) {
        const userMessageTextArea = textareas[0];
        const currentHeight = parseInt(userMessageTextArea.style.height, 10) || 0;
        if (userMessageTextArea.scrollHeight > currentHeight) {
          userMessageTextArea.style.height = userMessageTextArea.scrollHeight + 4 + 'px';
        }
      }
    }
  }, []);

  const handleClickCloseTooltip = useCallback(() => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  }, [showTooltip]);

  const clearForm = useCallback(() => {
    setInputValues((prevValues) => ({
      ...prevValues,
      moveDate: '',
      numBaths: '',
      numBeds: '',
    }));
  }, []);

  // Effect for component mount (componentDidMount)
  useEffect(() => {
    updateTextAreaHeight();
    if (isMobile) {
      window.addEventListener('click', handleClickCloseTooltip);
    }

    // Cleanup function (componentWillUnmount)
    return () => {
      window.removeEventListener('click', handleClickCloseTooltip);
    };
  }, [isMobile, updateTextAreaHeight, handleClickCloseTooltip]);

  // Effect for contactSubmitted changes (UNSAFE_componentWillReceiveProps)
  useEffect(() => {
    if (contactSubmitted) {
      clearForm();
    }
  }, [contactSubmitted, clearForm]);

  // Effect for textarea height updates (componentDidUpdate)
  useEffect(() => {
    updateTextAreaHeight();
  }, [updateTextAreaHeight]);

  // Effect for cached data pending changes
  useEffect(() => {
    if (!isCachedDataPending) {
      const newValues = calculateInitialValues();
      setInputValues({
        userMessage: newValues.userMessage,
        userEmail: newValues.userEmail || '',
        userName: userInquiryDataCache?.name || newValues.userInfoFirstLastName || '',
        userPhoneNumber: userInquiryDataCache?.phone || '',
        numBaths: newValues.numBaths,
        numBeds: newValues.numBeds,
        moveDate: dateUtils.formatDateMmddyyyy(new Date()),
        lowIncomeMessage: null,
        requestToApply: false,
      });
    }
  }, [isCachedDataPending, calculateInitialValues, userInquiryDataCache]);

  const handleMouseOver = useCallback(() => {
    if (isMobile) {
      // hover unavailable for mweb - use onClick instead
      return;
    }
    setShowTooltip(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) {
      // hover unavailable for mweb - use onClick instead
      return;
    }
    setShowTooltip(false);
  }, [isMobile]);

  const handleTooltipClick = useCallback(
    (e: React.MouseEvent) => {
      // mWeb: prevent closing and opening of tooltip (HPWEB-5430)
      e.stopPropagation();

      if (!isMobile) {
        // hover unavailable for mweb - use onClick instead
        return;
      }
      setShowTooltip(!showTooltip);
    },
    [isMobile, showTooltip],
  );

  const setError = useCallback((type: string, msg: string) => {
    setInputErrors((prevErrors) => ({
      ...prevErrors,
      [type]: msg,
    }));
  }, []);

  const handleEmailBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const emailInput = e.target.value;
      const newInputValuesObj = assign({}, inputValues);
      newInputValuesObj.userEmail = emailInput;
      setInputValues(newInputValuesObj);

      if (controller.userHasMaskedEmail(inputValues.userEmail)) {
        setError('userEmail', 'Please enter a valid email address.');
      }

      mailcheck.run({
        email: emailInput,
        suggested: (suggestion) => {
          const mailcheckSuggestionElement = (
            <span
              className="suggestion"
              onClick={() => {
                const newEmail = suggestion.full;
                const newInputObj = assign({}, inputValues);
                newInputObj.userEmail = newEmail;
                setInputValues(newInputObj);
                setMailcheckHint('');
              }}
            >
              Did you mean <Linker>{suggestion.full}</Linker>?
            </span>
          );
          setMailcheckHint(mailcheckSuggestionElement);
        },
        empty: () => {
          setMailcheckHint('');
        },
      });
    },
    [inputValues, setError],
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const emailInput = e.target.value;
      const newInputValuesObj = assign({}, inputValues);
      newInputValuesObj.userEmail = emailInput;
      setInputValues(newInputValuesObj);
    },
    [inputValues],
  );

  const handleEditUserInfo = useCallback(() => {
    setEditingUserInfo(true);
  }, []);

  const handleCalendarInput = useCallback(
    (date: string) => {
      const newInputValuesObj = assign({}, inputValues);
      newInputValuesObj.moveDate = date;
      setInputValues(newInputValuesObj);
    },
    [inputValues],
  );

  const handleTextInput = useCallback(
    (field: keyof ContactFormInputValues, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newInputValuesObj = assign({}, inputValues);
      newInputValuesObj[field] = e.target.value as any;
      setInputValues(newInputValuesObj);
    },
    [inputValues],
  );

  const handleLowIncomeSelect = useCallback(
    (value: string) => {
      const newInputValuesObj = assign({}, inputValues);
      newInputValuesObj.lowIncomeMessage = value;
      setInputValues(newInputValuesObj);
    },
    [inputValues],
  );

  const handleApplyChange = useCallback(() => {
    const newInputValuesObj = assign({}, inputValues);
    newInputValuesObj.requestToApply = !newInputValuesObj.requestToApply;
    setInputValues(newInputValuesObj);
  }, [inputValues]);

  const handleRestrictedConfirmationSelect = useCallback((value: boolean) => {
    setRestrictedConfirmation(value);
  }, []);

  const validateInput = useCallback(
    (inputValuesToValidate: ContactFormInputValues) => {
      const { errorObj, isValid } = controller.validateContactFormInput({
        inputValues: inputValuesToValidate,
        listing,
        restrictedConfirmation,
      });

      setInputErrors(errorObj.inputErrors);
      setFormError(errorObj.formError);
      return isValid;
    },
    [listing, restrictedConfirmation],
  );

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      // if validation methods found an error, bail out of the submit process and show an error.
      if (!validateInput(inputValues)) {
        return;
      }

      onSubmitForm(inputValues);
    },
    [inputValues, validateInput, onSubmitForm],
  );

  const handleBedsBathsSelect = useCallback(
    ({ beds, baths }: { beds: string; baths: string }) => {
      const newInputValuesObj = assign({}, inputValues);
      newInputValuesObj.numBeds = beds;
      newInputValuesObj.numBaths = baths;
      setInputValues(newInputValuesObj);
    },
    [inputValues],
  );

  // Early return for loading states
  const isContactFormNotReadyForRender = !clientLoaded || isCachedDataPending === true;
  if (isContactFormNotReadyForRender) {
    return null;
  }

  const { contact, isApartmentBldg, rentalApplicationStatus, waitlisted } = listing;

  const phoneRequired = contact.requiresPhone;
  const userInfoFirstLastName = `${user.info.firstName} ${user.info.lastName}`.trim();
  const userName = userInquiryDataCache.name || userInfoFirstLastName;
  const userEmail = listingUtils_getContactEmail(user, userInquiryDataCache);
  const userPhone = userInquiryDataCache.phone;
  const isMissingUserInfo = !user.loggedIn || !userName || !userEmail || (phoneRequired && !userPhone);
  const maskedUserEmail = controller.userHasMaskedEmail(userEmail);
  let sendButtonLabel = 'Send message';

  if (waitlisted) {
    sendButtonLabel = 'Add me to the waitlist';
  }

  return (
    <>
      <form className={cx('ContactForm', { 'ContactForm-popup': isPopup })}>
        <StyledContactListingRestrictions
          onSelect={handleLowIncomeSelect}
          onConfirmation={handleRestrictedConfirmationSelect}
          listing={listing}
          isPopup={isPopup}
        />
        {contact.requiresMoveDate && (
          <StyledCalendarContainer>
            <MoveInCalendar
              help={inputErrors.moveDate}
              hpxStyle={inputErrors.moveDate ? 'error' : null}
              onCalendarInput={handleCalendarInput}
              required
              toggleSubmitDisabled={() => {
                setIsDisabled(!isDisabled);
              }}
              value={inputValues.moveDate}
            />
          </StyledCalendarContainer>
        )}

        {contact.requiresNumBeds && (
          <BedsBathsSelect
            listing={listing}
            onBedsBathsSelect={handleBedsBathsSelect}
            beds={inputValues.numBeds}
            baths={inputValues.numBaths}
          />
        )}
        {(contact.requiresMoveDate || contact.requiresNumBeds) && <div className="ContactForm-delim" />}
        <Input
          id="user-message-input"
          help={inputErrors.userMessage}
          hpxStyle={inputErrors.userMessage ? 'error' : null}
          label="Customize message"
          maxLength={750}
          onChange={handleTextInput.bind(null, 'userMessage')}
          ref={userMessageInput}
          required={contact.requiresMessage}
          type="textarea"
          value={inputValues.userMessage}
        />

        {!isApartmentBldg && shouldShowRequestToApplyButton(rentalApplicationStatus) && (
          <Fragment>
            <div
              className="ContactForm-ready-top"
              onMouseOver={handleMouseOver}
              onMouseLeave={handleMouseLeave}
              onClick={handleTooltipClick}
            >
              <ReadyToApplyText size="sm">Ready to apply?</ReadyToApplyText>
              <img src={showTooltip ? IconMoreInfoActive : IconMoreInfo} width="16px" height="16px" />
            </div>
            {showTooltip && (
              <StyledContactFormTipText className="ContactForm-tip">
                We&apos;ll let them know that you&apos;d like information regarding their application process. You are
                in no way obligated to apply.
              </StyledContactFormTipText>
            )}
            <div className="ContactForm-ready-bottom">
              <StyledCheckbox
                label="Send me an application form"
                checked={Boolean(inputValues.requestToApply)}
                onChange={handleApplyChange}
                textSize="md"
              />
            </div>
          </Fragment>
        )}

        {isMissingUserInfo || editingUserInfo ? (
          <Fragment>
            <Input
              id="name-input"
              className="ContactForm-name-padding"
              help={inputErrors.userName}
              hpxStyle={inputErrors.userName ? 'error' : null}
              label="Your name"
              maxLength={50}
              onChange={handleTextInput.bind(null, 'userName')}
              required={contact.requiresName}
              value={inputValues.userName}
              autoComplete="name"
            />
            <StyledEmailInput
              id="email-input"
              help={emailErrorMessage || inputErrors.userEmail || mailcheckHint}
              hpxStyle={inputErrors.userEmail ? 'error' : null}
              label="Your email"
              maxLength={50}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              required={contact.requiresEmail}
              name="email"
              type="email"
              value={inputValues.userEmail}
              autoComplete="email"
            />
            <StyledInputPhoneNumber
              id="phone-input"
              help={phoneErrorMessage || inputErrors.userPhoneNumber}
              hpxStyle={inputErrors.userPhoneNumber ? 'error' : null}
              label="Your phone number"
              maxLength={15}
              onChange={handleTextInput.bind(null, 'userPhoneNumber')}
              required={contact.requiresPhone}
              value={inputValues.userPhoneNumber}
              autoComplete="tel-national"
            />
          </Fragment>
        ) : (
          <ContactUserInfo
            onEditUserInfo={handleEditUserInfo}
            emailErrorMessage={emailErrorMessage}
            phoneErrorMessage={phoneErrorMessage}
          />
        )}
        <StyledSubmitContainer>
          <Button
            full
            size="lg"
            btnType="text-color"
            onClick={handleSubmit}
            disabled={isLoading || maskedUserEmail || isDisabled}
          >
            {isLoading ? 'Sending Message..' : sendButtonLabel}
          </Button>
          {(errorMessage || formError) && (
            <Row>
              <StyledAlertDiv>{errorMessage || formError}</StyledAlertDiv>
            </Row>
          )}
        </StyledSubmitContainer>
      </form>
    </>
  );
};

export default ContactForm;
